import React from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { Locales } from '@carsys/enums/locales'
import useQuery from '@cbgms/base/http/use-query'
import { CountryDropdownItem } from '@carsys/form/src/CountriesDropdownBase'
import { CountriesDropdownType } from './enum'
import { getCountries, getEnvironmentCountries, getSupportedCountries } from './actions'
import { mapAllCountries, mapSupportedCountryToDropdownOptions } from './selectors'
import { Time } from '@carsys/enums/time'

interface Props extends CountriesOptionsWrapperProps {
  type: CountriesDropdownType
}
interface CountriesOptionsWrapperProps {
  children: (countryOptions: CountryDropdownItem[]) => JSX.Element
}

export const CountriesOptionsWrapper: React.FC<Props> = ({ type, children }) => {
  const { i18n } = useTranslation()
  const dispatch = useDispatch()

  const loadCountries = useQuery(
    ['GET_COUNTRIES', type, i18n.language],
    async () => {
      if (type === CountriesDropdownType.all) {
        const { Data } = await dispatch(getCountries(i18n.language as Locales))
        return mapAllCountries(Data)
      }

      if (type === CountriesDropdownType.onlySupported) {
        const { Data } = await dispatch(getSupportedCountries())
        return Data
      }

      if (type === CountriesDropdownType.onlyEnvironment) {
        const { Data } = await dispatch(getEnvironmentCountries())
        return Data
      }

      return null
    },
    {
      // This was cached before we removed the global staleTime.
      // To not break any existing behavior we set the staleTime here explicitely.
      staleTime: 30 * Time.Second
    }
  )

  const countryOptions = React.useMemo(() => {
    if (!loadCountries.data || loadCountries.data.length === 0) return []
    return mapSupportedCountryToDropdownOptions(loadCountries.data)
  }, [type, loadCountries.data])

  return children(countryOptions)
}

export const CountriesOptionsWrapperAllCountries: React.FC<CountriesOptionsWrapperProps> = ({ children }) => {
  return <CountriesOptionsWrapper type={CountriesDropdownType.all}>{children}</CountriesOptionsWrapper>
}
